import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { FEATURE_FLAGS } from '../../consts/features';
import { GIT_PROVIDERS } from '../../consts/git';
import { useUser } from '../../hooks/useUser';
import { useWizardAnalytics } from '../../hooks/useWizardAnalytics';
import { Gitlab } from '../../logos';
import { GitlabWebhookInstallResult } from '../../network/httpClients/GitlabClient';
import { QUERY_KEYS } from '../../network/queries/consts';
import { useIsFeatureEnabledQuery } from '../../network/queries/useIsFeatureEnabledQuery';
import { ROUTES } from '../../routes/consts';
import {
  DialogTitleWithIcon,
  MultiStepDialog,
} from '../../shared/MultiStepDialog';
import { spawn } from '../../utils/translations';
import { getMTSteps, getMTTokenSteps } from './configuration';
import { GitlabInstallationContext } from './gitlabInstallationContext';
import { GitlabAuthType } from './types';
import {
  getDefaultFurthestStepIndex,
  getIntegrationsCloseTarget,
} from './utils';

export const GitlabMTInstallationDialog = () => {
  const [appConfigId, setAppConfigId] = useState<string>();
  const [isConnectionSuccessful, setIsConnectionSuccessful] = useState(false);
  const [webhookResult, setWebhookResult] =
    useState<GitlabWebhookInstallResult>();
  const [totalSucceededCount, setTotalSucceededCount] = useState(0);
  const [isGitlabAppConfigured, setIsGitlabAppConfigured] = useState(false);
  const [authType, setAuthType] = useState<GitlabAuthType>('oauth');

  const {
    isGitlabMTCloudInstallationWizardAvailable,
    isGitlabMTCloudInstallationWizardLoading,
  } = useUser();
  // GAT is gated behind its own flag on MT too; PAT stays disabled ("Coming
  // soon") here — MT is OAuth + optional GAT only.
  const { data: isGroupTokenAuthFlagEnabled } = useIsFeatureEnabledQuery(
    FEATURE_FLAGS.GITLAB_GROUP_TOKEN_AUTH,
  );
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const spawnGeneral = spawn('dialogs.gitlab-installation.general');
  // Picking GAT swaps in the short token wizard; OAuth keeps the full MT flow.
  const steps = authType === 'gat' ? getMTTokenSteps() : getMTSteps();
  const {
    onStepViewed,
    onStepNavigated,
    onCancel: trackClose,
    onWizardCompleted,
  } = useWizardAnalytics({
    gitProvider: GIT_PROVIDERS.GITLAB,
    wizardVersion: 'v1',
    steps,
  });

  const defaultFurthestStepIndex = getDefaultFurthestStepIndex(
    location.pathname,
    location.search,
    ROUTES.GITLAB_MT_INSTALLATION_STEP_4,
    steps,
  );

  const close = () => {
    navigate(getIntegrationsCloseTarget());
  };

  const onCancel = (stepIndex: number, stepId: string) => {
    trackClose(stepIndex, stepId);
    close();
  };

  const onComplete = () => {
    onWizardCompleted();
    close();
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.INSTALLATIONS_COUNTS],
    });
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.GIT_INSTALLATIONS],
    });
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.GIT_REPOSITORIES],
    });
  };

  if (
    !isGitlabMTCloudInstallationWizardAvailable &&
    !isGitlabMTCloudInstallationWizardLoading
  ) {
    console.warn("You're not allowed to access this page");

    return <Navigate to={ROUTES.ROOT} />;
  }

  return (
    <GitlabInstallationContext.Provider
      value={{
        // MT offers OAuth + optional GAT. PAT stays disabled ("Coming soon")
        // here — isTokenAuthEnabled is hard-off on MT.
        authType,
        setAuthType,
        isTokenAuthEnabled: false,
        isGroupTokenAuthEnabled: Boolean(isGroupTokenAuthFlagEnabled),
        appConfigId,
        setAppConfigId,
        isConnectionSuccessful,
        setIsConnectionSuccessful,
        webhookResult,
        setWebhookResult,
        totalSucceededCount,
        setTotalSucceededCount,
        isGitlabAppConfigured,
        setIsGitlabAppConfigured,
      }}
    >
      <MultiStepDialog
        // Remount when the auth type changes so the dialog's internal
        // furthestStepReached/completedSteps reset (mirrors the ST wizard).
        key={authType}
        title={
          <DialogTitleWithIcon text={spawnGeneral('title')}>
            <Gitlab size={24} />
          </DialogTitleWithIcon>
        }
        steps={steps}
        onCancel={onCancel}
        onComplete={onComplete}
        onStepViewed={onStepViewed}
        onStepNavigated={onStepNavigated}
        className="sm:max-w-250!"
        contentClassName="text-body"
        disableOutsideClick={true}
        disableEscapeKey={true}
        defaultFurthestStepIndex={defaultFurthestStepIndex}
      />
    </GitlabInstallationContext.Provider>
  );
};
